export interface NotificationResponse {
  mailSettings: NotificationData;
  textSetting?: EmailNotificationData;
}

export interface NotificationData {
  editorRoleId: number;
  updatedAt: string;
  groups: NotificationGroupData[];
  isCustomNotificationSettings?: boolean;
}

export interface EmailNotificationData {
  editorRoleId: number;
  updatedAt: string;
  settings: NotificationsObject;
}

export interface NotificationGroupData {
  groupId: number;
  name: string;
  settings: NotificationsObject;
  order?: number;
}

export interface NotificationGroupSettings {
  groupId: number;
  name: string;
  settings: NotificationSetting;
  order?: number;
}

export interface NotificationsObject {
  [id: number]: NotificationValue;
}

export interface NotificationValue {
  name: string;
  enabled: boolean;
  order?: number;
}

export interface NotificationCheckbox {
  id: number;
  value: boolean;
  name: string;
  hidden: boolean;
  order?: number;
}

export interface NotificationSetting {
  [id: number]: {
    value: boolean;
    name: string;
    hidden: boolean;
    order?: number;
  };
}

export enum EMAIL_NOTIFICATIONS {
  CERT_ASSIGNED_TO_PROFESSIONAL = 1,
  CERT_ENABLE_FOR_COMPLETION,
  CERT_FILE_WALLET,
  CERT_APPROVED_JOB,
  CERT_REJECTED_JOB,
  CERT_AVAILABLE,
  CERT_DISABLED,
  CERT_REMOVE_TRACK_REQUEST,
  CERT_TRACK_REQUEST,
  CERT_ASSIGNED_BY_EMPLOYER_TO_PROF,
  CERT_DETAILS_CANCELLED,
  FILE_REGENERATION,
  TR_CERT_ACCEPTED,
  TR_CERT_COMPLETED,

  STEP_YELLOW,
  STEP_RED,
  STEP_PASSES_DUE_DATE,
  STEP_THREE_DAYS_LEFT,
  STEP_TWO_DAYS_LEFT,
  STEP_ONE_DAYS_LEFT,
  TR_CERT_IS_EXPIRED,
  JOB_STEP_APPROVED,
  JOB_STEP_REJECTED,
  STEP_PAID_BY_EMPLOYER,

  PROFILE_DEACTIVATED,
  PROFILE_ACTIVATED,
  PROF_INFO_CHANGE_REQUEST_APPROVED,
  PROF_INFO_CHANGE_REQUEST_REJECTED,

  SUPERVISOR_ACCEPTED_INVITATION,
  SUPERVISOR_INVITATION_EXPIRED_TO_PROF,
  PROF_LOG_STEP_COMPLETED_BY_SUPERVISOR,
}

export enum EMAIL_NOTIFICATIONS_GROUPS {
  GENERAL_NOTIFICATIONS = 1,
  STEP_NOTIFICATIONS,
  PROFILE_NOTIFICATIONS
}

export const APPLY_TO_USERS_WARNING =
  'This action will update the Email Notification Settings for every user in your organization. Are you sure you want to continue?';

export const RESET_DEFAULT_SETTINGS =
  'This action will return the Email Notification Settings to the defaults selected by your administrators. Are you sure you want to continue?';

export const GENERAL_NOTIFICATIONS = {
  [EMAIL_NOTIFICATIONS.CERT_ASSIGNED_TO_PROFESSIONAL]: 'New credential assigned',
  [EMAIL_NOTIFICATIONS.CERT_ENABLE_FOR_COMPLETION]: 'Enabled credential',
  [EMAIL_NOTIFICATIONS.CERT_FILE_WALLET]: 'Credential file added to Digital Wallet',
  [EMAIL_NOTIFICATIONS.CERT_APPROVED_JOB]: 'File uploaded on credential accept confirmed',
  [EMAIL_NOTIFICATIONS.CERT_REJECTED_JOB]: 'File uploaded on credential accept rejected',
  [EMAIL_NOTIFICATIONS.CERT_AVAILABLE]: 'Credential paused, but available for completion',
  [EMAIL_NOTIFICATIONS.CERT_DISABLED]: 'Credential paused and disabled',
  [EMAIL_NOTIFICATIONS.CERT_REMOVE_TRACK_REQUEST]: 'Employer revoked credential tracking',
  [EMAIL_NOTIFICATIONS.CERT_TRACK_REQUEST]: 'Employer request to track credential',
  [EMAIL_NOTIFICATIONS.CERT_ASSIGNED_BY_EMPLOYER_TO_PROF]: 'Assignment of new credential by Employer',
  [EMAIL_NOTIFICATIONS.CERT_DETAILS_CANCELLED]: 'Cancelled credential from detailed view',
  [EMAIL_NOTIFICATIONS.FILE_REGENERATION]: 'Credential file regeneration',
  [EMAIL_NOTIFICATIONS.TR_CERT_ACCEPTED]: 'Acceptance of Test Registration credential',
  [EMAIL_NOTIFICATIONS.TR_CERT_COMPLETED]: 'Completed Test Registration Credential',
};

export const STEP_NOTIFICATIONS = {
  [EMAIL_NOTIFICATIONS.STEP_YELLOW]: 'Step is nearing its due date',
  [EMAIL_NOTIFICATIONS.STEP_RED]: 'Step is almost at due date',
  [EMAIL_NOTIFICATIONS.STEP_PASSES_DUE_DATE]: 'Step past due date',
  [EMAIL_NOTIFICATIONS.STEP_THREE_DAYS_LEFT]: '3 days to step due date',
  [EMAIL_NOTIFICATIONS.STEP_TWO_DAYS_LEFT]: '2 days to step due date',
  [EMAIL_NOTIFICATIONS.STEP_ONE_DAYS_LEFT]: '1 day to step due date',
  [EMAIL_NOTIFICATIONS.TR_CERT_IS_EXPIRED]: 'Credential of type Test Registration has expired',
  [EMAIL_NOTIFICATIONS.JOB_STEP_APPROVED]: 'Approved step completion',
  [EMAIL_NOTIFICATIONS.JOB_STEP_REJECTED]: 'Rejected step completion',
  [EMAIL_NOTIFICATIONS.STEP_PAID_BY_EMPLOYER]: 'Step has been paid by Employer',
};

export const PROFILE_NOTIFICATIONS = {
  [EMAIL_NOTIFICATIONS.PROFILE_DEACTIVATED]: 'Profile deactivated',
  [EMAIL_NOTIFICATIONS.PROFILE_ACTIVATED]: 'Profile activated',
  [EMAIL_NOTIFICATIONS.PROF_INFO_CHANGE_REQUEST_APPROVED]: 'Request to change account information was approved',
  [EMAIL_NOTIFICATIONS.PROF_INFO_CHANGE_REQUEST_REJECTED]: 'Request to change account information was rejected',
};

export const LOG_STEP_NOTIFICATIONS = {
  [EMAIL_NOTIFICATIONS.SUPERVISOR_ACCEPTED_INVITATION]: 'Supervisor has accepted invitation',
  [EMAIL_NOTIFICATIONS.SUPERVISOR_INVITATION_EXPIRED_TO_PROF]: 'Supervisor invitation has expired',
  [EMAIL_NOTIFICATIONS.PROF_LOG_STEP_COMPLETED_BY_SUPERVISOR]: 'Supervisor completed step',
};
